import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { noop } from 'ui-components/default-props/noop';
import { Text, TextType } from 'ui-components/components/text';

import Global from '../../../store/global-selectors';
import ModalPortal from '../../../components/modal-portal';
import { MultiPackageModal } from '../../../components/modals/multi-package-modal';
import { useFulfillmentPlannerContext } from '../../context';
import { useModal } from '../../../hooks/use-modal';
import { useShouldRequirePackageContentDescription } from '../../package-content-description';

import { messages } from './messages';
import style from './multi-package-button.module.less';

/**
 * @param {object} props
 * @param {string} props.buttonClassName
 * @param {string} props.contentClassName
 * @param {string} props.iconClassName
 * @param {() => void} props.onTrackOpenMultiPackageModal
 * @returns {React.ReactNode}
 */
export const MultiPackageButton = ({ buttonClassName, contentClassName, iconClassName, onTrackOpenMultiPackageModal = noop }) => {
  const { isOpen, open, close } = useModal();
  const {
    hasInsuranceProviderSelected,
    onMultiPackageSave,
    packages: orderPackages,
    serviceIdAndSellerLabelProviderId,
  } = useFulfillmentPlannerContext();

  const supportsMultiPackage = useSelector((state) => {
    const service =
      Global.selectSellerServiceById(state, {
        serviceId: serviceIdAndSellerLabelProviderId?.serviceId,
      }) || {};
    return service.features?.supportsMultiPackage ?? false;
  });

  const shouldRequirePackageDescription = useShouldRequirePackageContentDescription();

  const onSave = useCallback(
    (packages) => {
      onMultiPackageSave(packages);
      close();
    },
    [close, onMultiPackageSave],
  );

  const onClickMultiPackageButton = useCallback(() => {
    onTrackOpenMultiPackageModal();
    open();
  }, [onTrackOpenMultiPackageModal, open]);

  return (
    <>
      <Button
        className={classnames(buttonClassName, style['multi-package-button'])}
        onClick={onClickMultiPackageButton}
        testId='multi-package-launcher'
        theme='ghost'
      >
        <FlexRow className={classnames(contentClassName, style['multi-package-button-content'])}>
          <FontAwesomeIcon className={classnames(iconClassName, style.icon)} icon={faPlus} />
          <Text textType={TextType.Caption} value={messages.addPackagesButtonLabel} />
        </FlexRow>
      </Button>
      {isOpen && (
        <ModalPortal onClose={close}>
          <MultiPackageModal
            hasInsuranceProviderSelected={hasInsuranceProviderSelected}
            hasMultiPackageSupport={supportsMultiPackage}
            initialPackages={orderPackages}
            onSave={onSave}
            shouldRequirePackageDescription={shouldRequirePackageDescription}
          />
        </ModalPortal>
      )}
    </>
  );
};
